/**
 * CommonDatePicker - Web端日期选择器组件
 *
 * @description 跨端通用日期选择器组件，提供Web端实现，基于Ant Design的DatePicker
 * @features
 * - 基于Ant Design DatePicker实现
 * - 支持自定义日期格式
 * - 支持自定义占位符
 * - 支持国际化
 * - 只读输入框，防止手动输入
 *
 * @usage
 * ```tsx
 * <CommonDatePicker
 *   value="2024-01-01"
 *   placeholder="请选择日期"
 *   onConfirm={(date) => console.log(date)}
 * />
 * ```
 */

import React, { useCallback, useMemo } from "react";
import type { DatePickerProps } from "antd";
import { DatePicker } from "@aspen/ui";
import { i18nCrossUtil } from "@adgm/libs";
import dayjs from "dayjs";
import type { TDatePickerWebProps } from "@adgm/cross-models";

/**
 * Web端日期选择器组件
 */
export const CommonDateTimePicker: React.FC<TDatePickerWebProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  style,
  allowClear = true,
}) => {
  const crossIntl = i18nCrossUtil.t();
  const defaultPlaceholder = placeholder || crossIntl["kyc.please.select"];

  /**
   * 处理日期变化
   */
  const handleChange: DatePickerProps["onChange"] = useCallback(
    (_, dateString: string) => {
      onChange?.(dateString);
    },
    [onChange],
  );

  // 将字符串日期转换为dayjs对象
  const dateValue = useMemo(() => {
    return value ? dayjs(value) : undefined;
  }, [value]);

  return (
    <>
      <style>{`
      .commonDatePicker {
        display: flex;
        flex: 1;
        border: 0 !important;
        background-color: var(--main-bg-color) !important;

        :global {
          .ant-picker {
            height: 46px !important;
          }
        }
      }
      `}</style>
      <div className="commonDatePicker">
        <DatePicker
          value={dateValue}
          style={style}
          inputReadOnly
          placeholder={defaultPlaceholder}
          disabled={disabled}
          allowClear={allowClear}
          onChange={handleChange}
        />
      </div>
    </>
  );
};
